//#region INTERFACE IGroupAccess
/**
 * Interface representing the access settings for multiple groups.
 * Each key is a group name mapped to its access configuration.
 *
 * @interface IGroupAccess
 * @description This interface defines the structure for storing access configurations for various groups, where each group name is mapped to its respective access settings.
 */
export type IGroupAccess = Record<string, IGroupAccessGroup>;

//#endregion

//#region INTERFACE IGroupAccessGroup
/**
 * Interface defining access settings for a specific group.
 *
 * @interface IGroupAccessGroup
 * @description This interface outlines the access settings for a single group, including the access code and the priority order in which the group is processed or displayed.
 */
export interface IGroupAccessGroup {
  /**
   * Numeric code that defines the level of access for the group.
   *
   * @type {number}
   * @example 1
   */
  accessCode: number;

  /**
   * Determines the group's display or execution priority.
   *
   * @type {number}
   * @example 1
   */
  orderPriority: number;
}

//#endregion

//#region INTERFACE IGroupAccessStore
/**
 * Interface representing group access data for a single store.
 *
 * @interface IGroupAccessStore
 * @description This interface stores the access details for a particular store, including the name of the store, the group assigned to the store, and the group's access code.
 */
export interface IGroupAccessStore {
  /**
   * Numeric code that determines the group's access rights in the store.
   * Null means remove this store from the user.
   *
   * @type {number | null}
   * @example 1
   */
  groupAccessCode: number | null;

  /**
   * The name of the group assigned access to the store.
   * Null when the store is being removed from the user.
   *
   * @type {string | null}
   * @example 'admin'
   */
  groupName: string | null;

  /**
   * The display name of the store.
   *
   * @type {string}
   * @example 'Store A'
   */
  storeName: string;
}

//#endregion

//#region INTERFACE IDefaultGroupAccessDocument
/**
 * Interface representing the default access configuration for a group.
 * Typically used for initializing group access settings.
 *
 * @interface IDefaultGroupAccessDocument
 * @description This interface defines the default access configuration for a group, including the access code and order priority.
 */
export interface IDefaultGroupAccessDocument {
  /**
   * Access code assigned to the group.
   *
   * @type {number}
   * @example 1
   */
  accessCode: number;

  /**
   * The name of the group.
   *
   * @type {string}
   * @example 'admin'
   */
  groupName: string;

  /**
   * The order in which the group appears or is processed.
   *
   * @type {number}
   * @example 1
   */
  orderPriority: number;
}

//#endregion

//#region INTERFACE IGroupAccessPerStore
/**
 * Interface representing access settings per store.
 * Each key is a store ID mapped to that store's group access configuration.
 *
 * @interface IGroupAccessPerStore
 * @description This interface defines the group access settings for individual stores, where each store is mapped to its respective group access data.
 */
export type IGroupAccessPerStore = Record<string, IGroupAccessStore>;

//#endregion
